import { useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, LoaderCircle, Upload } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { ImportMedicineRow, ImportResult } from '@/api/hooks/useMedicine';
import { useImportMedicines } from '@/api/hooks/useMedicine';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const NUMERIC_FIELDS = new Set(['stock', 'minStock', 'price', 'costPrice', 'gstRate']);

const COLUMNS = [
  'name', 'shortName', 'genericName', 'composition', 'category', 'unit', 'stock', 'minStock',
  'price', 'costPrice', 'gstRate', 'expiryDate', 'batchNo', 'location', 'barcode',
  'supplierName', 'supplierContact', 'supplierEmail',
];

/** Splits one CSV line, tolerating quoted fields with embedded commas. */
function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i] ?? '';
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
}

function parseCsv(text: string): ImportMedicineRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  const [headerLine, ...dataLines] = lines;
  if (!headerLine || dataLines.length === 0) return [];

  const header = splitCsvLine(headerLine).map((h) => h.trim());

  return dataLines.map((line) => {
    const cells = splitCsvLine(line);
    const row: Record<string, string | number | undefined> = {};

    header.forEach((col, i) => {
      if (!COLUMNS.includes(col)) return;
      const raw = cells[i]?.trim();
      if (!raw) return;
      row[col] = NUMERIC_FIELDS.has(col) ? Number(raw) : raw;
    });

    return row as ImportMedicineRow;
  });
}

const TEMPLATE_HEADER = COLUMNS.join(',');

/**
 * CSV bulk import for medicines. Parsed client-side (no server round trip per
 * row) then submitted as one batch; the server validates row-by-row and
 * reports what it skipped, so a typo in row 40 doesn't sink rows 1-39.
 */
export function BulkImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const importMedicines = useImportMedicines();

  const [rows, setRows] = useState<ImportMedicineRow[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function handleFile(file: File) {
    setParseError(null);
    setResult(null);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.length === 0) {
        setParseError('No data rows found. Check the file has a header row plus at least one medicine.');
        return;
      }
      setRows(parsed);
      setFileName(file.name);
    } catch {
      setParseError('Could not read this file. Make sure it is a plain CSV.');
    }
  }

  async function handleImport() {
    if (!rows) return;
    try {
      const outcome = await importMedicines.mutateAsync(rows);
      setResult(outcome);
    } catch (err) {
      setParseError(err instanceof ApiError ? err.message : 'Import failed. Please try again.');
    }
  }

  function handleClose(next: boolean) {
    if (!next) {
      setRows(null);
      setFileName('');
      setParseError(null);
      setResult(null);
      importMedicines.reset();
    }
    onOpenChange(next);
  }

  function downloadTemplate() {
    const blob = new Blob([`${TEMPLATE_HEADER}\n`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'medicines-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Bulk import medicines
          </DialogTitle>
        </DialogHeader>

        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />

        {result ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-status-ok/30 bg-status-ok/10 px-3 py-2 text-sm">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-status-ok" />
              {result.created} medicine(s) added, {result.skipped} skipped.
            </div>
            {result.errors.length > 0 && (
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                {result.errors.map((err) => (
                  <p key={`${err.row}-${err.name}`} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-status-low" />
                    Row {err.row} ({err.name}): {err.error}
                  </p>
                ))}
              </div>
            )}
          </div>
        ) : rows ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {fileName} — {rows.length} row(s) ready to import.
            </p>
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2 text-xs">
              {rows.slice(0, 10).map((row, i) => (
                <p key={i} className="truncate">
                  {row.name || '(no name)'} · {row.batchNo || '—'} · qty {row.stock ?? 0}
                </p>
              ))}
              {rows.length > 10 && <p className="text-muted-foreground">…and {rows.length - 10} more</p>}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border py-10 text-center">
            <Upload className="h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Upload a CSV with a header row of column names.</p>
            <div className="flex gap-2">
              <Button type="button" onClick={() => inputRef.current?.click()}>
                Choose CSV
              </Button>
              <Button type="button" variant="outline" onClick={downloadTemplate}>
                Download template
              </Button>
            </div>
          </div>
        )}

        {parseError && (
          <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
            {parseError}
          </p>
        )}

        <DialogFooter>
          {rows && !result && (
            <>
              <Button type="button" variant="outline" onClick={() => setRows(null)}>
                Choose a different file
              </Button>
              <Button type="button" onClick={handleImport} disabled={importMedicines.isPending}>
                {importMedicines.isPending && <LoaderCircle className="animate-spin" />}
                Import {rows.length} row(s)
              </Button>
            </>
          )}
          {result && (
            <Button type="button" onClick={() => handleClose(false)}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
